import { combineReducers, createSlice } from '@reduxjs/toolkit'

import { mapResponse } from 'shared/api/lib/mapResponse'
import { settingsApi } from 'shared/api/requests/settings'
import {
  GetOrdersListResponse,
  Order,
} from 'shared/api/requests/settings/schemas/GetOrderListSchema'
import { createListSlice, InitialItemWithLimiter } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'

interface OrderWithExtraInfo extends InitialItemWithLimiter {
  list: Order[]
  ordersInfoByStatus: GetOrdersListResponse['orders_info_by_status']
  successedOrdersInfo: GetOrdersListResponse['successed_orders_info_by_currency']
}

const operations = createListOperations({
  getList: mapResponse(settingsApi.order.getList, (response) => {
    return {
      list: response.data.orders_list,
      limiter: response.data.limiter,
      ordersInfoByStatus: response.data.orders_info_by_status,
      successedOrdersInfo: response.data.successed_orders_info_by_currency,
    }
  }),
  getNextList: mapResponse(settingsApi.order.getList, (response) => {
    return {
      list: response.data.orders_list,
      limiter: response.data.limiter,
      ordersInfoByStatus: response.data.orders_info_by_status,
      successedOrdersInfo: response.data.successed_orders_info_by_currency,
    }
  }),
  getPrevList: undefined,
  createItem: undefined,
  deleteItem: undefined,
  updateItem: undefined,
})

export const ordersListSlice = createListSlice<
  Order,
  OrderWithExtraInfo,
  typeof operations.getList.arguments,
  typeof operations.getNextList.arguments,
  Record<string, never>,
  Record<string, never>,
  Record<string, never>,
  Record<string, never>,
  Record<string, never>,
  Order['order_id'],
  TSFixMe,
  Record<string, never>,
  TSFixMe
>({
  storePath: 'orders.list',
  namespace: 'orders',

  operations: operations,

  getItemId: (item: Order) => item.order_id,

  extraState: {},
  reducers: {},
  listKey: 'list',
})

export type OrdersInfoByStatus = GetOrdersListResponse['orders_info_by_status']
export type SuccessedOrdersInfoByCurrency =
  GetOrdersListResponse['successed_orders_info_by_currency']

export interface ExtraOrdersInfoState {
  ordersInfoByStatus: OrdersInfoByStatus | null
  successedOrdersInfo: SuccessedOrdersInfoByCurrency | null
}

const initialState: ExtraOrdersInfoState = {
  ordersInfoByStatus: null,
  successedOrdersInfo: null,
}

export const extraOrdersInfoSlice = createSlice({
  name: 'extraOrdersInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(ordersListSlice.actions.getListFx.fulfilled, (state, action) => {
      state.ordersInfoByStatus =
        action?.payload && 'ordersInfoByStatus' in action.payload
          ? action.payload.ordersInfoByStatus
          : null
      state.successedOrdersInfo =
        action?.payload && 'successedOrdersInfo' in action.payload
          ? action.payload.successedOrdersInfo
          : null
    })
  },
})

export const ordersReducer = combineReducers({
  list: ordersListSlice.reducer,
  extraInfo: extraOrdersInfoSlice.reducer,
})

export const ordersActions = {
  ...ordersListSlice.actions,
  ...extraOrdersInfoSlice.actions,
}

export type OrdersState = ReturnType<typeof ordersReducer>
